import api from "./axiosConfig";
import { getApiErrorPayload } from "../utils/apiError";

export interface StaffMember {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  isActive: boolean;
  clientCount: number;
  createdAt: string;
  lastLoginAt?: string | null;
}

export interface CreateStaffRequest {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  role: string;
}

export interface InviteStaffRequest {
  firstName: string;
  lastName: string;
  email: string;
  role: string;
}

export interface UpdateStaffRequest {
  firstName?: string;
  lastName?: string;
  role?: string;
  isActive?: boolean;
}

export interface StaffClientSummary {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  status: string;
}

export function staffApiErrorMessage(err: unknown): string | null {
  return getApiErrorPayload(err)?.message ?? null;
}

export function staffApiErrorCode(err: unknown): string | undefined {
  return getApiErrorPayload(err)?.code;
}

export const staffApi = {
  list: async (): Promise<StaffMember[]> => {
    const { data } = await api.get<StaffMember[]>("/staff");
    return data;
  },

  get: async (id: string): Promise<StaffMember> => {
    const { data } = await api.get<StaffMember>(`/staff/${id}`);
    return data;
  },

  create: async (payload: CreateStaffRequest): Promise<StaffMember> => {
    const { data } = await api.post<StaffMember>("/staff", payload);
    return data;
  },

  invite: async (payload: InviteStaffRequest): Promise<StaffMember> => {
    const { data } = await api.post<StaffMember>("/staff/invite", payload);
    return data;
  },

  update: async (id: string, payload: UpdateStaffRequest): Promise<StaffMember> => {
    const { data } = await api.put<StaffMember>(`/staff/${id}`, payload);
    return data;
  },

  deactivate: async (id: string): Promise<void> => {
    await api.delete(`/staff/${id}`);
  },

  getClients: async (staffId: string): Promise<StaffClientSummary[]> => {
    const { data } = await api.get<StaffClientSummary[]>(`/staff/${staffId}/clients`);
    return data;
  },

  assignClient: async (staffId: string, clientId: string): Promise<void> => {
    await api.post(`/staff/${staffId}/assign-client`, { clientId });
  },
};
